import { Box, Typography } from "@mui/material";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { format, parseISO, isValid } from "date-fns";
import { useAppTheme } from "@/hooks/useAppTheme";
import { currencyCompactNotation, formatUSD } from "@/utils/formatters/currencyFormatter";
import { ClaimItem } from "@/types";

const AMOUNT_COLOR_LIGHT = "#0E4A5F";
const AMOUNT_COLOR_DARK = "#79B7C5";

export const ClaimsOverTimeChart = ({ claimItems }: { claimItems: ClaimItem[] }) => {
  const { theme, isDarkMode } = useAppTheme();
  const primaryColor = theme.palette.primary.main;
  const amountColor = isDarkMode ? AMOUNT_COLOR_DARK : AMOUNT_COLOR_LIGHT;

  if (!claimItems || claimItems.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <Typography variant="body2" color="text.secondary">
          No claims activity data
        </Typography>
      </Box>
    );
  }

  // Group items by YYYY-MM
  const grouped: Record<string, { count: number; total: number }> = {};

  claimItems.forEach((item) => {
    const dateValue = item.date_of_service_start ?? item.date_of_service;
    const paidAmount = item.paid_amount;

    if (!dateValue || typeof paidAmount !== "number") return;

    const parsedDate = parseISO(dateValue);
    if (!isValid(parsedDate)) return;

    const dateKey = format(parsedDate, "yyyy-MM");
    if (!grouped[dateKey]) grouped[dateKey] = { count: 0, total: 0 };

    grouped[dateKey].count += 1;
    grouped[dateKey].total += paidAmount;
  });

  if (Object.keys(grouped).length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <Typography variant="body2" color="text.secondary">
          No claims with valid dates
        </Typography>
      </Box>
    );
  }

  const data = Object.entries(grouped)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateKey, { count, total }]) => ({
      name: format(parseISO(`${dateKey}-01`), "MMM yy"),
      claims: count,
      amount: total,
    }));

  return (
    <ResponsiveContainer key={`chart-${data.length}`} width="100%" height={220}>
      <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
        <defs>
          <linearGradient id="claimsCountGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={primaryColor} stopOpacity={0.2} />
            <stop offset="95%" stopColor={primaryColor} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="claimsAmountGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={amountColor} stopOpacity={0.15} />
            <stop offset="95%" stopColor={amountColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
        <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => formatUSD(value, currencyCompactNotation)}
        />
        <Tooltip
          contentStyle={{
            borderRadius: 8,
            border: `1px solid ${theme.palette.divider}`,
            fontSize: 12,
            backgroundColor: theme.palette.background.paper,
          }}
          formatter={(value: number, name: string) => [
            name === "amount" ? formatUSD(value) : value,
            name === "amount" ? "Total Paid" : "Claim Items",
          ]}
        />
        <Area
          yAxisId="left"
          type="monotone"
          dataKey="claims"
          stroke={primaryColor}
          strokeWidth={2}
          fill="url(#claimsCountGradient)"
          dot={false}
          activeDot={{ r: 4, strokeWidth: 0, fill: primaryColor }}
          isAnimationActive={true}
          animationDuration={1200}
          animationEasing="ease-in-out"
        />
        <Area
          yAxisId="right"
          type="monotone"
          dataKey="amount"
          stroke={amountColor}
          strokeWidth={2}
          fill="url(#claimsAmountGradient)"
          dot={false}
          isAnimationActive={true}
          animationDuration={1200}
          animationEasing="ease-in-out"
          animationBegin={200}
          activeDot={{ r: 4, strokeWidth: 0, fill: amountColor }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};
